import { type ChangeEvent, type FC, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { usePersonPhotoUpload } from "@/shared/hooks/usePersonPhotoUpload";
import { usePersonPhotoUrl } from "@/shared/hooks/usePersonPhotoUrl";

import { sentryUtils } from "@/shared/utils/sentry";
import fallbackImage from "@/shared/assets/backgrounds/sunset.png";

import type { EstateRow } from "../hooks/useEstateSummaryRecords";
import { formatLongDate } from "../summaryFormat";

interface SummaryHeaderProps {
  estate: EstateRow;
}

export const SummaryHeader: FC<SummaryHeaderProps> = ({ estate }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { photoUrl, refreshPhotoUrl } = usePersonPhotoUrl(estate.estate_id);
  const { uploadPersonPhoto } = usePersonPhotoUpload();

  const name = estate.full_name || "";
  const birth = formatLongDate(estate.date_of_birth);
  const death = formatLongDate(estate.date_of_death);
  const dates = birth && death ? `${birth} to ${death}` : birth || death;

  const handlePhotoSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const fileKey = await uploadPersonPhoto(file, estate.estate_id);
      if (!fileKey) return;
      await refreshPhotoUrl();
      await queryClient.invalidateQueries({ queryKey: ["all-person-photos"] });
      enqueueSnackbar("Profile photo updated", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Couldn't update profile photo. Please try again.", { variant: "error" });
      sentryUtils.captureError(err, "Summary photo upload failed", { estateId: estate.estate_id });
    }
  };

  return (
    <div className="flex items-center gap-6 rounded-lg border border-primary bg-white p-6">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        aria-label={photoUrl ? "Change photo" : "Add photo"}
        className="flex size-[120px] shrink-0 items-center justify-center overflow-hidden rounded border border-[#ddd7d5] bg-[#ddd7d5] p-[3px] max-sm:size-24"
      >
        <img src={photoUrl || fallbackImage} alt={name} className="size-full rounded-[3px] object-cover" />
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <p className="text-sm text-secondary">Remembering</p>
        <h2 className="truncate">{name}</h2>
        {dates && <p className="text-sm text-secondary">{dates}</p>}
      </div>
      {!photoUrl && (
        <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
          Add photo
        </Button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoSelect}
      />
    </div>
  );
};
